import { createPillButton } from '../ui/pillButton.js';
import { boardStatPreviewHtml, type BoardDecisionCopy } from './boardDecisionCopy.js';

export interface CasinoBetResult {
  betNumber: number | null;
  stake: number;
}

/**
 * Modale casino : radios 1–6 + mise centrée. Si `maxStake === 0`, tout est
 * grisé sauf « Ne pas miser ».
 */
export function openCasinoBetModal(
  host: HTMLElement,
  maxStake: number,
  multiplier: number
): Promise<CasinoBetResult> {
  return new Promise((resolve) => {
    const canBet = maxStake > 0;
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop confirm-modal board-casino-modal';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');
    backdrop.setAttribute('aria-labelledby', 'casino-modal-title');

    const numbersHtml = [1, 2, 3, 4, 5, 6]
      .map(
        (n) => `
      <label class="board-casino-number">
        <input type="radio" name="casino-bet-number" value="${n}" ${n === 1 ? 'checked' : ''} ${
          canBet ? '' : 'disabled'
        } />
        <span class="board-casino-number__face">${n}</span>
      </label>`
      )
      .join('');

    backdrop.innerHTML = `
      <div class="modal confirm-modal-dialog board-casino-modal__dialog">
        <div class="modal-header">
          <h2 id="casino-modal-title">Casino</h2>
          <button type="button" class="btn btn-ghost confirm-modal-close" aria-label="Fermer">×</button>
        </div>
        <div class="modal-body board-casino-modal__body${canBet ? '' : ' is-broke'}">
          <p class="board-casino-modal__lead">
            Choisissez un numéro puis votre mise. Gain = mise × ${multiplier} si le dé tombe dessus, sinon la mise est perdue.
          </p>
          ${
            canBet
              ? ''
              : `<p class="board-casino-modal__broke-hint">Vous n’avez pas de points de course — impossible de miser.</p>`
          }
          <fieldset class="board-casino-numbers" ${canBet ? '' : 'disabled'}>
            <legend class="board-casino-legend">Numéro</legend>
            <div class="board-casino-numbers__grid" role="radiogroup" aria-label="Numéro du dé">
              ${numbersHtml}
            </div>
          </fieldset>
          <div class="board-casino-stake">
            <label class="board-casino-stake__label" for="casino-bet-stake">Mise</label>
            <div class="board-casino-stake__chip">
              <span class="board-casino-stake__currency" aria-hidden="true">♦</span>
              <input
                class="board-casino-stake__input"
                type="number"
                id="casino-bet-stake"
                min="0"
                max="${maxStake}"
                value="0"
                step="1"
                inputmode="numeric"
                ${canBet ? '' : 'disabled'}
                aria-describedby="casino-stake-hint"
              />
            </div>
            <p id="casino-stake-hint" class="board-casino-stake__hint">
              ${canBet ? `De 0 à ${maxStake} points de course` : 'Solde insuffisant'}
            </p>
          </div>
        </div>
        <div class="modal-footer confirm-modal-footer"></div>
      </div>`;

    const stakeInput = backdrop.querySelector('#casino-bet-stake') as HTMLInputElement;
    const footer = backdrop.querySelector('.confirm-modal-footer') as HTMLElement;
    const skipBtn = createPillButton('Ne pas miser', { variant: 'soft' });
    const betBtn = createPillButton('Miser', {
      variant: 'cta',
      disabled: !canBet,
    });
    if (!canBet) {
      betBtn.title = 'Pas assez de points de course';
      betBtn.setAttribute('aria-disabled', 'true');
    }
    footer.append(skipBtn, betBtn);

    let settled = false;
    const finish = (value: CasinoBetResult): void => {
      if (settled) return;
      settled = true;
      backdrop.remove();
      resolve(value);
    };

    const selectedNumber = (): number => {
      const checked = backdrop.querySelector<HTMLInputElement>(
        'input[name="casino-bet-number"]:checked'
      );
      return Number(checked?.value ?? 1);
    };

    skipBtn.addEventListener('click', () => finish({ betNumber: null, stake: 0 }));
    betBtn.addEventListener('click', () => {
      if (!canBet) return;
      const stake = Math.max(0, Math.min(maxStake, Math.floor(Number(stakeInput.value)) || 0));
      if (stake <= 0) {
        finish({ betNumber: null, stake: 0 });
        return;
      }
      finish({ betNumber: selectedNumber(), stake });
    });
    backdrop
      .querySelector('.confirm-modal-close')
      ?.addEventListener('click', () => finish({ betNumber: null, stake: 0 }));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) finish({ betNumber: null, stake: 0 });
    });
    backdrop.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') finish({ betNumber: null, stake: 0 });
    });

    host.appendChild(backdrop);
    if (canBet) stakeInput.focus();
    else (skipBtn as HTMLButtonElement).focus();
  });
}

/**
 * Modale de décision plateau (amélioration / malus / achat…) : texte long +
 * aperçu d’étoiles type garage (delta vert/rouge).
 */
export function openBoardDecisionModal(
  host: HTMLElement,
  copy: BoardDecisionCopy
): Promise<boolean> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop confirm-modal board-decision-modal';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');
    backdrop.setAttribute('aria-labelledby', 'board-decision-modal-title');

    const paragraphsHtml = copy.paragraphs
      .map((p) => `<p class="board-decision-modal__p">${escapeText(p)}</p>`)
      .join('');
    const previewHtml = copy.preview ? boardStatPreviewHtml(copy.preview) : '';

    backdrop.innerHTML = `
      <div class="modal confirm-modal-dialog board-decision-modal__dialog">
        <div class="modal-header">
          <h2 id="board-decision-modal-title"></h2>
          <button type="button" class="btn btn-ghost confirm-modal-close" aria-label="Fermer">×</button>
        </div>
        <div class="modal-body board-decision-modal__body">
          ${paragraphsHtml}
          ${previewHtml}
        </div>
        <div class="modal-footer confirm-modal-footer"></div>
      </div>`;

    const titleEl = backdrop.querySelector('#board-decision-modal-title') as HTMLElement;
    titleEl.textContent = copy.title;

    const footer = backdrop.querySelector('.confirm-modal-footer') as HTMLElement;
    const confirmBtn = createPillButton(copy.confirmLabel, {
      variant: 'cta',
      disabled: !copy.canAfford && !copy.ackOnly,
    });
    if (!copy.ackOnly) {
      const cancelBtn = createPillButton(copy.cancelLabel, { variant: 'soft' });
      if (!copy.canAfford) {
        confirmBtn.title = 'Pas assez de points de course';
        confirmBtn.setAttribute('aria-disabled', 'true');
        const hint = document.createElement('p');
        hint.className = 'board-decision-modal__afford-hint';
        hint.textContent = 'Pas assez de points de course pour payer.';
        backdrop.querySelector('.board-decision-modal__body')?.appendChild(hint);
      }
      footer.append(cancelBtn, confirmBtn);
      cancelBtn.addEventListener('click', () => finish(false));
      (cancelBtn as HTMLButtonElement).focus();
    } else {
      footer.append(confirmBtn);
      (confirmBtn as HTMLButtonElement).focus();
    }

    let settled = false;
    const finish = (value: boolean): void => {
      if (settled) return;
      settled = true;
      backdrop.remove();
      resolve(value);
    };

    confirmBtn.addEventListener('click', () => {
      if (!copy.ackOnly && !copy.canAfford) return;
      finish(true);
    });
    backdrop
      .querySelector('.confirm-modal-close')
      ?.addEventListener('click', () => finish(copy.ackOnly ? true : false));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) finish(copy.ackOnly ? true : false);
    });
    backdrop.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') finish(copy.ackOnly ? true : false);
    });

    host.appendChild(backdrop);
  });
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
